'use client';

import { useEffect, useRef, useState } from 'react';

import {
  DEFERRED_MOUNT_IDLE_TIMEOUT_MS,
  DEFERRED_MOUNT_ROOT_MARGIN,
} from '@/constants/deferredMount';
import { scheduleIdle } from '@/lib/scheduleIdle';

type UseDeferredMountOptions = {
  /** When false, never mounts (caller renders a static fallback). Default true. */
  enabled?: boolean;
  /** IntersectionObserver `rootMargin`. */
  margin?: string;
  /** `requestIdleCallback` timeout (ms). */
  idleTimeoutMs?: number;
};

/**
 * Gate mounting of non-critical client islands until near the viewport **or**
 * the main thread is idle. Attach `ref` to a same-sized placeholder so layout
 * is reserved (no CLS) before the heavy leaf mounts.
 */
export function useDeferredMount<T extends Element = HTMLDivElement>({
  enabled = true,
  margin = DEFERRED_MOUNT_ROOT_MARGIN,
  idleTimeoutMs = DEFERRED_MOUNT_IDLE_TIMEOUT_MS,
}: UseDeferredMountOptions = {}) {
  const ref = useRef<T | null>(null);
  const [nearView, setNearView] = useState(false);
  const [idle, setIdle] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setNearView(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin: margin, threshold: 0 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled, margin]);

  useEffect(() => {
    if (!enabled) return;

    return scheduleIdle(() => setIdle(true), idleTimeoutMs);
  }, [enabled, idleTimeoutMs]);

  const ready = enabled && (nearView || idle);

  return { ref, ready, nearView, idle };
}
